import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import AddConfigModal from "../../components/AddConfigModal";
import { staticDataService } from "../../utils/services/static-data.service";
import DashboardSettings from "../../components/dashboard-settings";

const AdminConfig = () => {
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState({});
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [defaultAdminConfigs, setDefaultAdminConfigs] = useState([]);

  useEffect(() => {
    fetchDefaultAdminConfigs();
  }, []);

  const fetchDefaultAdminConfigs = async () => {
    try {
      const response = await staticDataService.fetchAllStaticData();
      setDefaultAdminConfigs(response?.data || []);
    } catch (error) {
      console.error("Fetch default configs failed:", error);
      setDefaultAdminConfigs([]);
    }
  };

  const handleKeyChange = (index, value) => {
    setSelectedConfig({
      ...selectedConfig,
      value: selectedConfig.value.map((x, i) => (i === index ? value : x)),
    });
  };

  const addKeyField = () => {
    setSelectedConfig({
      ...selectedConfig,
      value: [...(selectedConfig.value || []), ""],
    });
  };

  const removeKeyField = (index) => {
    setSelectedConfig({
      ...selectedConfig,
      value: (selectedConfig.value || []).filter((_, i) => i !== index),
    });
  };

  const onClose = () => {
    setEditingConfig(null);
    setSelectedConfig({});
  };

  const handleSaveConfig = async () => {
    try {
      const payload = {
        type: selectedConfig.type,
        value: selectedConfig.value,
      };
      if (selectedConfig._id) {
        await staticDataService.updateStaticData(selectedConfig._id, payload);
      }
      toast.success("Config updated successfully!");
      fetchDefaultAdminConfigs();
      setEditingConfig(null);
      setSelectedConfig({});
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }
  };

  const handleAddNewConfig = async ({ type, value }) => {
    try {
      await staticDataService.create({ type, value });
      toast.success("Config created successfully!");
      fetchDefaultAdminConfigs();
      setShowAddNewModal(false);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }
  };

  const startEditing = (config) => {
    setSelectedConfig({
      ...config,
      value: Array.isArray(config.value) ? [...config.value] : config.value,
    });
    setEditingConfig(config.type);
  };

  const getConfigValue = (config) => {
    if (typeof config?.value === "boolean") return config.value;
    if (Array.isArray(config?.value)) return config.value.join(", ");
    return config?.value !== undefined ? String(config.value) : "Not set";
  };

  const renderConfigField = (config) => {
    const isEditing = editingConfig === config.type;
    const configKey = config.type;

    return (
      <Box
        key={configKey}
        sx={{
          borderRadius: 2,
          padding: 3,
          backgroundColor: "var(--bg-paper)",
          width: "100%",
          border: "1px solid var(--border-color)",
          position: "relative",
          mb: 2,
        }}
      >
        {!isEditing && (
          <IconButton
            onClick={() => startEditing(config)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <DriveFileRenameOutlineIcon sx={{ color: "#FF5733" }} />
          </IconButton>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 14,
                fontWeight: 500,
                textAlign: "left",
                color: "var(--text-tertiary)",
              }}
            >
              {config.type}
            </Typography>
          </Grid>

          <Grid item xs={12} md={7}>
            {isEditing ? (
              <>
                {typeof selectedConfig.value === "boolean" ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Switch
                      checked={selectedConfig.value}
                      onChange={(e) =>
                        setSelectedConfig({
                          ...selectedConfig,
                          value: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                    <Typography
                      sx={{
                        fontFamily: "poppins",
                        fontSize: 13,
                        color: "var(--text-secondary)",
                      }}
                    >
                      {selectedConfig.value ? "True" : "False"}
                    </Typography>
                  </Box>
                ) : Array.isArray(selectedConfig.value) ? (
                  <>
                    {selectedConfig.value?.map((key, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={index}
                        sx={{ mb: 1 }}
                      >
                        <Grid item xs={10}>
                          <TextField
                            fullWidth
                            value={key}
                            placeholder={`Value ${index + 1}`}
                            onChange={(e) =>
                              handleKeyChange(index, e.target.value)
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton onClick={() => removeKeyField(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        onClick={addKeyField}
                        sx={{
                          textTransform: "none",
                          fontSize: 13,
                          fontFamily: "poppins",
                          mt: 1,
                        }}
                      >
                        + Add more
                      </Button>
                    </Box>
                  </>
                ) : (
                  <TextField
                    fullWidth
                    type={
                      typeof selectedConfig.value === "number"
                        ? "number"
                        : "text"
                    }
                    value={selectedConfig.value}
                    onChange={(e) =>
                      setSelectedConfig({
                        ...selectedConfig,
                        value: e.target.value,
                      })
                    }
                    size="small"
                  />
                )}

                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      mr: 2,
                      color: "red",
                      borderColor: "red",
                      fontFamily: "poppins",
                      fontSize: 14,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveConfig}
                    sx={{ fontFamily: "poppins", fontSize: 14 }}
                  >
                    Save
                  </Button>
                </Box>
              </>
            ) : typeof config?.value === "boolean" ? (
              <Chip
                label={config.value ? "True" : "False"}
                size="small"
                color={config.value ? "success" : "error"}
                variant="outlined"
                sx={{ fontFamily: "poppins", fontSize: 12, fontWeight: 600 }}
              />
            ) : (
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontSize: 14,
                  fontWeight: 400,
                  textAlign: "start",
                  color: "var(--text-secondary)",
                  wordBreak: "break-word",
                }}
              >
                {getConfigValue(config)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "90vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "var(--scrollbar-thumb)",
          borderRadius: 4,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "var(--scrollbar-thumb-hover)",
        },
        backgroundColor: "var(--bg-paper)",
        padding: 1
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          position: "sticky",
          top: 0,
          backgroundColor: "var(--bg-paper)",
          zIndex: 1,
          py: 2,
        }}
      >
        <CustomTypography
          sx={{
            textAlign: "start",
            fontWeight: 600,
            fontSize: 24,
            color: "var(--text-tertiary)",
          }}
        >
          Configuration Settings
        </CustomTypography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddNewModal(true)}
          sx={{
            fontFamily: "poppins",
            fontSize: 14,
            textTransform: "none",
            backgroundColor: "#007DFF",
            "&:hover": { backgroundColor: "#0056CC" },
          }}
        >
          Config
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        {defaultAdminConfigs.map((config) => renderConfigField(config))}
      </Box>

      <DashboardSettings />

      <AddConfigModal
        open={showAddNewModal}
        onClose={() => setShowAddNewModal(false)}
        onSave={handleAddNewConfig}
      />
    </Box>
  );
};

export default AdminConfig;
